import React, { useState } from 'react';
import moment, { Moment } from 'moment-timezone';
import 'react-dates/initialize';
import { SingleDatePicker } from 'react-dates';
import type {
  ArchivingFilterNode,
  ArchivingMessageSearchResponse,
} from 'js-sdk/src/apis/ArchivingAPI';
import BEM from 'common/bem';
import mobxInjectSelect from 'common/utils/mobxInjectSelect';

const classes = BEM.with('MessageSearch');

type ArchivedMessageResult = {
  messageId?: string;
  sentAt?: string;
  senderDisplayName?: string;
  recipientAccountId?: string;
  body?: string;
  hasAttachment?: boolean;
};

const TIME_RANGE_PRESETS = [
  { value: 'last24h', label: 'Last 24 hours' },
  { value: 'last7d', label: 'Last 7 days' },
  { value: 'last30d', label: 'Last 30 days' },
  { value: 'thisQuarter', label: 'This quarter' },
  { value: 'custom', label: 'Custom range' },
] as const;

type TimeRangePreset = (typeof TIME_RANGE_PRESETS)[number]['value'];

/** Splits a comma-separated input into trimmed, non-empty account IDs. */
function parseAccountIds(input: string): string[] {
  return input
    .split(',')
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
}

/**
 * Builds the AND/OR filter tree sent to analytics-service from whichever of these fields the user
 * actually filled in. Each filled field becomes one leaf, ANDed together; an empty field is simply
 * omitted rather than turned into a leaf that matches everything. hasAttachmentOnly is a toggle, not
 * a tri-state field — off means "don't filter on attachments" (both matched), never "match only
 * messages without one" — so it only ever contributes a leaf when true.
 */
function buildFilterTree(
  senderAccountIds: string[],
  recipientAccountIds: string[],
  hasAttachmentOnly: boolean
): ArchivingFilterNode | undefined {
  const clauses: ArchivingFilterNode[] = [];
  if (senderAccountIds.length > 0) {
    clauses.push({ field: 'senderAccountId', operator: 'IN', values: senderAccountIds });
  }
  if (recipientAccountIds.length > 0) {
    clauses.push({ field: 'recipientAccountId', operator: 'IN', values: recipientAccountIds });
  }
  if (hasAttachmentOnly) {
    clauses.push({ field: 'hasAttachment', operator: 'EQ', values: ['true'] });
  }

  if (clauses.length === 0) return undefined;
  if (clauses.length === 1) return clauses[0];
  return { op: 'AND', clauses };
}

/**
 * Computes the {startDate, endDate} ISO-8601 bounds for a time range preset. For "custom", bounds
 * are only returned once both endpoints are picked — an incomplete custom range applies no date
 * filter rather than blocking search, since search has no required fields.
 */
function computeDateRange(
  preset: TimeRangePreset,
  customStartDate: Moment | null,
  customEndDate: Moment | null
): { startDate?: string; endDate?: string } {
  const now = moment();
  switch (preset) {
    case 'last24h':
      return {
        startDate: moment(now).subtract(24, 'hours').toISOString(),
        endDate: now.toISOString(),
      };
    case 'last7d':
      return {
        startDate: moment(now).subtract(7, 'days').toISOString(),
        endDate: now.toISOString(),
      };
    case 'last30d':
      return {
        startDate: moment(now).subtract(30, 'days').toISOString(),
        endDate: now.toISOString(),
      };
    case 'thisQuarter':
      return {
        startDate: moment(now).startOf('quarter').toISOString(),
        endDate: now.toISOString(),
      };
    case 'custom':
      if (!customStartDate || !customEndDate) return {};
      return {
        startDate: moment(customStartDate).startOf('day').toISOString(),
        endDate: moment(customEndDate).endOf('day').toISOString(),
      };
    default:
      return {};
  }
}

type MessageSearchProps = {};

type MobxProps = {
  client: {
    archiving: {
      searchMessages: (
        organizationId: string,
        query: string,
        options?: {
          startDate?: string;
          endDate?: string;
          hasAttachment?: boolean;
          senderAccountIds?: string[];
          recipientAccountIds?: string[];
          filter?: ArchivingFilterNode;
          limit?: number;
        }
      ) => Promise<ArchivingMessageSearchResponse>;
    };
  } | null;
  currentOrganizationId: string;
};

type CombinedProps = MessageSearchProps & MobxProps;

export function MessageSearchInner({ client, currentOrganizationId }: CombinedProps) {
  const [query, setQuery] = useState('');
  const [senderAccountIdsInput, setSenderAccountIdsInput] = useState('');
  const [recipientAccountIdsInput, setRecipientAccountIdsInput] = useState('');
  const [hasAttachmentOnly, setHasAttachmentOnly] = useState(false);
  const [timeRangePreset, setTimeRangePreset] = useState<TimeRangePreset>('last24h');
  const [customStartDate, setCustomStartDate] = useState<Moment | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Moment | null>(null);
  const [customStartFocused, setCustomStartFocused] = useState(false);
  const [customEndFocused, setCustomEndFocused] = useState(false);
  const [results, setResults] = useState<ArchivedMessageResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!client) return;

    setIsLoading(true);
    setError(null);
    try {
      const { startDate, endDate } = computeDateRange(
        timeRangePreset,
        customStartDate,
        customEndDate
      );
      const senderAccountIds = parseAccountIds(senderAccountIdsInput);
      const recipientAccountIds = parseAccountIds(recipientAccountIdsInput);
      const filter = buildFilterTree(senderAccountIds, recipientAccountIds, hasAttachmentOnly);
      const response = await client.archiving.searchMessages(currentOrganizationId, query, {
        limit: 25,
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(filter && { filter }),
      });
      setResults(response?.data?.results || []);
    } catch (err) {
      setError('Something went wrong searching archived messages. Please try again.');
      setResults([]);
    } finally {
      setIsLoading(false);
      setHasSearched(true);
    }
  }

  return (
    <div className={classes()}>
      <div className={classes('header')}>
        <div className={classes('header-text')}>
          <h2 className={classes('title')}>Message Search</h2>
          <p className={classes('subtitle')}>Search archived messages for this organization</p>
        </div>
      </div>

      <form className={classes('search-bar')} onSubmit={onSubmit}>
        <input
          className={classes('search-input')}
          data-test-id="message-search-input"
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search archived messages…"
          type="text"
          value={query}
        />
        <button
          className={classes('search-button')}
          data-test-id="message-search-submit"
          disabled={isLoading}
          type="submit"
        >
          {isLoading ? 'Searching…' : 'Search'}
        </button>
      </form>

      <div className={classes('filters')}>
        <input
          className={classes('filter-input')}
          data-test-id="message-search-sender-account-ids"
          onChange={(event) => setSenderAccountIdsInput(event.target.value)}
          placeholder="Sender account ID(s), comma-separated"
          type="text"
          value={senderAccountIdsInput}
        />
        <input
          className={classes('filter-input')}
          data-test-id="message-search-recipient-account-ids"
          onChange={(event) => setRecipientAccountIdsInput(event.target.value)}
          placeholder="Recipient account ID(s), comma-separated"
          type="text"
          value={recipientAccountIdsInput}
        />
        <label className={classes('attachment-toggle')}>
          <input
            checked={hasAttachmentOnly}
            data-test-id="message-search-has-attachment-toggle"
            onChange={(event) => setHasAttachmentOnly(event.target.checked)}
            type="checkbox"
          />
          <span className={classes('attachment-toggle-label')}>Has attachment only</span>
        </label>
      </div>

      <div className={classes('time-range')}>
        <span className={classes('time-range-label')}>Time range</span>
        <div
          className={classes('time-range-chips')}
          data-test-id="message-search-time-range"
          role="group"
        >
          {TIME_RANGE_PRESETS.map((preset) => (
            <button
              className={classes('time-range-chip', {
                selected: timeRangePreset === preset.value,
              })}
              data-test-id={`message-search-time-range-${preset.value}`}
              key={preset.value}
              onClick={() => setTimeRangePreset(preset.value)}
              type="button"
            >
              {preset.label}
            </button>
          ))}
        </div>

        {timeRangePreset === 'custom' && (
          <div className={classes('custom-range')}>
            <SingleDatePicker
              date={customStartDate}
              displayFormat="MM/DD/YYYY"
              focused={customStartFocused}
              hideKeyboardShortcutsPanel
              id="message-search-custom-start-date"
              isOutsideRange={(day: Moment) => customEndDate != null && day.isAfter(customEndDate)}
              numberOfMonths={1}
              onDateChange={setCustomStartDate}
              onFocusChange={({ focused }: { focused: boolean }) => setCustomStartFocused(focused)}
              placeholder="From"
            />
            <SingleDatePicker
              date={customEndDate}
              displayFormat="MM/DD/YYYY"
              focused={customEndFocused}
              hideKeyboardShortcutsPanel
              id="message-search-custom-end-date"
              isOutsideRange={(day: Moment) =>
                customStartDate != null && day.isBefore(customStartDate)
              }
              numberOfMonths={1}
              onDateChange={setCustomEndDate}
              onFocusChange={({ focused }: { focused: boolean }) => setCustomEndFocused(focused)}
              placeholder="To"
            />
          </div>
        )}
      </div>

      {error && <div className={classes('error')}>{error}</div>}

      {!error && hasSearched && !isLoading && results.length === 0 && (
        <div className={classes('empty-state')}>No messages found.</div>
      )}

      {results.length > 0 && (
        <ul className={classes('results')} data-test-id="message-search-results">
          {results.map((result, index) => (
            <li className={classes('result')} key={result.messageId || index}>
              <div className={classes('result-meta')}>
                <span className={classes('result-sender')}>{result.senderDisplayName}</span>
                {result.recipientAccountId && (
                  <span className={classes('result-recipient')}>
                    to {result.recipientAccountId}
                  </span>
                )}
                <span className={classes('result-date')}>
                  {result.sentAt && moment(result.sentAt).format('MMM D, YYYY h:mm A')}
                </span>
              </div>
              <div className={classes('result-body')}>{result.body}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default mobxInjectSelect<MessageSearchProps, MobxProps>({
  messengerStore: ['client', 'currentOrganizationId'],
})(MessageSearchInner);
